import * as React from 'react'
import { FC, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import {
  ColorSelector,
  PREDEFINED_COLORS,
} from '@user/components/Settings/D2D/ManageKnockStatus/ColorSelector'
import {
  salesTeamsForCurrentUser,
  selectSalesTeamById,
  useGetSalesTeamListOnMount,
} from '@user/store/salesTeam'
import {
  createTerritory,
  splitTerritory,
  updateTerritory,
} from '@user/store/territory/api'
import { Territory, TerritoryBoundary } from '@user/store/territory/typings'
import { TerritoryPreviewPanel } from './TerritoryPreviewPanel'
import { TerritoryOverlapWarning } from './TerritoryOverlapWarning'
import { StoreData } from '@user/store/reducers/typings'

/** Matches TerritoryInputValidator.MaxNameLength on the server. */
const MAX_NAME_LENGTH = 100

/**
 * Put the server's geometry complaints into words a sales admin can act on.
 *
 * Everything else is passed through untouched -- "A territory named X already exists." is
 * already plain. Only NetTopologySuite's vocabulary leaks engineering at the user, and the
 * client normally catches a crossing before the request, so this is the backstop for the cases
 * it does not (simplification artefacts, rings too large for the client-side check).
 */
function humanizeSaveError(message: string): string {
  if (/self-intersection/i.test(message)) {
    return 'The outline crosses over itself. Draw a loop that finishes near where it started, without crossing your own line.'
  }
  if (/not a valid polygon/i.test(message)) {
    return 'That shape could not be turned into an area. Try drawing a simple loop around the places you want.'
  }
  return message
}

type Props = {
  /**
   * The geometry to save. For a new territory this is what was just drawn; for an edit it is
   * either the existing boundary from the cache or, after a redraw, the replacement. Absent
   * only when splitting by a line, where the halves do not exist until the server cuts them.
   */
  boundary?: TerritoryBoundary | null
  /** Present when editing an existing territory, absent when creating a new one. */
  territory?: Territory
  /**
   * The territory's shape BEFORE this redraw, so the preview can show what moves. Absent when
   * creating, and absent when editing details without touching the boundary -- in both cases
   * there is nothing to compare against.
   */
  previousBoundary?: TerritoryBoundary | null
  /**
   * The territory this shape is being carved OUT OF. Present means the save splits that
   * territory rather than creating a free-standing one -- same form, same preview, different
   * destination for the shape.
   */
  splitFrom?: Territory | null
  /**
   * The cut drawn across the territory, when splitting by line rather than by tracing a piece.
   * The shape of each half is only known once the server has divided it, so the preview panel
   * stands down for this case rather than describing a polygon that does not exist yet.
   */
  splitLine?: number[][] | null
  onClose: () => void
  onSaved: () => void
}

/**
 * Names the territory the admin just drew and saves it (WWM-17508, Feature A).
 *
 * Sales team is asked for before owners because that is the order product specified: an admin
 * picks the team, then picks owners from that team. It is also what the server enforces --
 * CreateAsync rejects an owner who is not a member of the chosen team -- so offering the full
 * user list here would invite a 400 the user cannot interpret.
 */
export const TerritoryCreateDialog: FC<Props> = ({
  boundary,
  territory,
  previousBoundary,
  splitFrom,
  splitLine,
  onClose,
  onSaved,
}) => {
  const isEdit = !!territory
  const isSplit = !!splitFrom
  const isLineSplit = isSplit && !!splitLine
  const dispatch = useAppDispatch()

  // The map never loads sales teams -- every other surface that needs them calls this on mount,
  // and without it this dropdown is silently empty, which is exactly what shipped first time.
  // includeUsers brings each team's positions and their members in the same request, so choosing
  // a team does not need a second round trip to fill Owners.
  const salesTeamStatus = useGetSalesTeamListOnMount({ includeUsers: true })
  const salesTeams = useSelector(salesTeamsForCurrentUser)

  // Seeded from the territory when editing. Safe as initialisers rather than an effect because
  // the caller mounts a fresh dialog per action, so these run exactly once per open.
  const [name, setName] = useState(territory?.name ?? '')
  const [colorHex, setColorHex] = useState<string>(
    territory?.colorHex ?? PREDEFINED_COLORS[4],
  )
  const [salesTeamId, setSalesTeamId] = useState<string>(
    territory ? String(territory.salesTeamId) : '',
  )
  const [ownerIds, setOwnerIds] = useState<string[]>(territory?.ownerIds ?? [])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // No reset effect: the caller mounts a fresh dialog per drawing, so this state starts clean.
  // Reusing one instance is what would risk carrying a previous name into the next save.

  const selectedTeam = useSelector((state: StoreData) =>
    salesTeamId ? selectSalesTeamById(state, Number(salesTeamId)) : undefined,
  )

  // One person can hold several positions on a team, so de-duplicate by user id.
  const teamMembers = useMemo(() => {
    const byId = new Map<string, string>()
    for (const position of selectedTeam?.positions ?? []) {
      for (const user of position.positionUsers ?? []) {
        if (user.isActive === false) continue
        if (!byId.has(user.userId)) byId.set(user.userId, user.userName)
      }
    }
    return [...byId.entries()].map(([userId, userName]) => ({
      userId,
      userName,
    }))
  }, [selectedTeam])

  const trimmedName = name.trim()
  const canSave =
    !!trimmedName && !!salesTeamId && (!!boundary || isLineSplit) && !saving

  const handleSave = async () => {
    if (!canSave) return

    setSaving(true)
    setError(null)

    const body = {
      name: trimmedName,
      salesTeamId: Number(salesTeamId),
      ownerIds,
      colorHex,
    }

    // Dispatched one branch at a time rather than through a ternary. The three thunks have
    // different argument and payload types, so a ternary makes TypeScript unify them and the
    // result is a union it cannot check -- which is what the `as never` here used to hide.
    const result = territory
      ? await dispatch(
          updateTerritory({ ...body, id: territory.id, boundary: boundary! }),
        )
      : splitFrom
        ? await dispatch(
            splitTerritory({
              ...body,
              id: splitFrom.id,
              // Exactly one of the two reaches the server. Sending both is refused rather
              // than resolved, so the client must not hedge.
              ...(isLineSplit ? { line: splitLine! } : { boundary: boundary! }),
            }),
          )
        : await dispatch(createTerritory({ ...body, boundary: boundary! }))

    setSaving(false)

    // rejected.match is a type guard, so `result.payload` below is the error shape rather than
    // an `any` nobody is checking.
    const failed =
      updateTerritory.rejected.match(result) ||
      splitTerritory.rejected.match(result) ||
      createTerritory.rejected.match(result)

    if (failed) {
      // createTerritory has already reduced the endpoint's three different failure shapes to one
      // readable sentence, preferring the server's own wording -- "Boundary is not a valid
      // polygon: Self-intersection." is actionable where "could not save" is not.
      setError(
        result.payload?.message
          ? humanizeSaveError(result.payload.message)
          : 'Could not save this territory. Please try again.',
      )
      return
    }

    onSaved()
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {isEdit
          ? 'Edit territory'
          : isSplit
            ? `Split off from ${splitFrom!.name}`
            : 'Name this territory'}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Above the fields on purpose: what the shape contains is the thing that decides
            whether to keep it, so it should be read before anything is typed. */}
        {isLineSplit ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            The larger side keeps &ldquo;{splitFrom!.name}&rdquo; and its
            owners. The smaller side becomes the territory you name here.
          </Alert>
        ) : (
          <>
            <TerritoryPreviewPanel
              boundary={boundary!}
              previousBoundary={previousBoundary}
            />
            {/* Only when this shape is actually new. Opening an existing territory to rename it
                would otherwise warn about an overlap the admin did not just draw and is not
                being asked to decide about -- noise on a form with one field in play. */}
            {(!territory || previousBoundary) && (
              <TerritoryOverlapWarning
                boundary={boundary!}
                // A split is carved OUT of its parent, so it overlaps it by construction; an
                // edit overlaps its own previous shape. Neither is news.
                excludeTerritoryId={territory?.id ?? splitFrom?.id ?? null}
              />
            )}
          </>
        )}

        <TextField
          label="Territory name"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, MAX_NAME_LENGTH))}
          required
          fullWidth
          autoFocus
          margin="normal"
          size="small"
          inputProps={{ maxLength: MAX_NAME_LENGTH }}
          data-test-id="territory-name-input"
        />

        <TextField
          label="Sales team"
          value={salesTeamId}
          onChange={(e) => {
            setSalesTeamId(e.target.value)
            // Owners belong to the previous team and may not be on this one, which the server
            // would reject. Clearing is less surprising than a save-time error.
            setOwnerIds([])
          }}
          select
          required
          fullWidth
          margin="normal"
          size="small"
          // An empty dropdown with no explanation is indistinguishable from a broken one, which
          // is how the missing fetch went unnoticed. Say which of the three this is.
          disabled={salesTeamStatus === 'pending'}
          error={salesTeamStatus === 'fail'}
          helperText={
            salesTeamStatus === 'pending'
              ? 'Loading sales teams…'
              : salesTeamStatus === 'fail'
                ? 'Could not load sales teams. Close and try again.'
                : salesTeams.length === 0
                  ? 'No sales teams available for your account.'
                  : undefined
          }
          data-test-id="territory-sales-team-select"
        >
          {salesTeams.map((team) => (
            <MenuItem key={team.id} value={String(team.id)}>
              {team.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Owners"
          value={ownerIds}
          onChange={(e) =>
            setOwnerIds(
              typeof e.target.value === 'string'
                ? e.target.value.split(',')
                : (e.target.value as unknown as string[]),
            )
          }
          select
          fullWidth
          margin="normal"
          size="small"
          disabled={!salesTeamId}
          helperText={
            !salesTeamId
              ? 'Pick a sales team first'
              : 'Leave empty to assign owners later'
          }
          SelectProps={{
            multiple: true,
            renderValue: (selected) =>
              (selected as string[])
                .map((id) => teamMembers.find((m) => m.userId === id)?.userName)
                .filter(Boolean)
                .join(', '),
          }}
          data-test-id="territory-owners-select"
        >
          {teamMembers.map((member) => (
            <MenuItem key={member.userId} value={member.userId}>
              <Checkbox
                checked={ownerIds.includes(member.userId)}
                size="small"
              />
              <ListItemText primary={member.userName} />
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Colour on the map
          </Typography>
          <ColorSelector value={colorHex} onChange={setColorHex} />
        </Box>
      </DialogContent>

      <DialogActions>
        {/*
          "Discard shape", not "Cancel". Two different Cancels in one flow is how a user ended
          up leaving draw mode when they meant to dismiss a message. This one throws away the
          drawing and returns to the map still in draw mode, ready for another attempt; the bar
          behind it says "Exit drawing" for the other intent.
        */}
        <Button onClick={onClose} disabled={saving}>
          {/*
            Editing discards unsaved changes, not a drawing, so "Discard shape" would be a lie.
            The two Cancels that confused a user earlier are why each label states its effect.
          */}
          {isEdit || isSplit ? 'Cancel' : 'Discard shape'}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!canSave}
          data-test-id="territory-save-button"
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving
            ? 'Saving\u2026'
            : isEdit
              ? 'Save changes'
              : isSplit
                ? 'Split'
                : 'Save territory'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
